/**
 * @author Tyrus Malmstrom <tyrus@playposit.org>
 * A plugin that connects VideoJS to the new Panopto embed API.
 *
 * Wraps the official Panopto Embed API.
 * @see https://support.panopto.com/s/article/Learn-About-Panopto-Embed-API
 */

/* eslint no-dupe-class-members: "off" */
/*
  eslint class-methods-use-this: ["off", {
    "exceptMethods": [
      "clearIframeInitializationInterval"
    ]
  }]
*/

import videojs from 'video.js';
import semver from 'semver';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';

import {
  loadScript,
} from '../../utilities/helpers/index.js';

const [
  Tech,
  registerTech,
  _isOnMobile,
] = [
  videojs.getTech('Tech'),
  videojs.registerTech || videojs.tech,
  videojs.browser.IS_IOS || videojs.browser.IS_NATIVE_ANDROID,
];

let hasUserInteraction = false;

/**
 * Normalize the base Panopto server name for the school.
 *
 * @param {string} serverUrl Server name for hosted Panopto server
 * @return {string} normalized server url
 */
function normalizePanoptoServerUrl(serverUrl) {
  return serverUrl?.replace('https://', '')?.replaceLast('/', '');
}

class Panopto extends Tech {
  constructor(options, ready) {
    super(options, ready);
    if (semver.lt(videojs.VERSION, '7.18.1')) {
      throw new Error(
        `This plugin requires the videojs version 7.18.1. Please upgrade from your current version: ${videojs.VERSION}`,
      );
    }
    if (!options.panoptoBaseUrl) {
      throw new Error('The required "panopto.panoptoBaseUrl" parameter is missing from VideoJS config object.');
    }
    this.options_.panoptoBaseUrl = normalizePanoptoServerUrl(options.panoptoBaseUrl);
    this.setupDebugging(options);
    this.el_.classList.add('element-wrapper');
    Object.assign(this.el_.style, {
      position: 'relative',
      height: '100%',
    });

    this.uuid = options.uuid || uuidv4();
    this.debug = options.debug;
    this.debugPrefix = options.debugPrefix || this.uuid;
    this.playerStartTime = options.playerStartTime || 0;

    this.log('this.playerStartTime: ', this.playerStartTime);
    this.log('constructor()', { options: this.options_ });
    Panopto.initArray.push(this);
    this.setPoster(this.options_.poster);
    if (this.options_.source) this.setSrc(this.options_.source);
    this.setTimeout(function initializePanoptoPlayerTimeout() {
      if (this.el_) {
        this.el_.parentNode.className += ' vjs-panopto';
        if (_isOnMobile) this.el_.parentNode.className += ' vjs-panopto-mobile';
        if (Panopto.isScriptReady) this.initializePanoptoPlayer();
        this.triggerReady();
      }
    }.bind(this));
  }

  /**
    * A function that initializes the child player state, which is simply an object containing relevant playback
    * information that we have received from the childPlayer.
    */
  initPanoptoPlayerState() {
    this.log('initPanoptoPlayerState()');
    this._panoptoPlayerState = {
      ended: false,
      playing: false,
      volume: this.options_.muted ? 0 : 1,
    };
  }

  /**
    * A function that logs a color-coded message to the console if `debug` is `true`.
    *
    * @param {Object} args - A destructred array of all arguments passed into the `log` function which are then logged out in that same order.
    *
    */
  log(...args) {
    /* eslint no-console: "off" */
    if (this.debug) console.log(`%c[${this.debugPrefix}]`, 'color: white; background: #2196F3', ...args);
  }

  /**
   * A function that creates the wrapper element that the Panopto tech resides within.
   */
  createEl() {
    const div = document.createElement('div');
    div.setAttribute('id', this.options_.techId);
    div.setAttribute('style', 'width:100%;height:100%;top:0;left:0;position:absolute');
    div.setAttribute('class', 'vjs-tech');
    return div;
  }

  /**
  * A function that initializes:
  * - the user interaction listeners,
  * - Panopto player
  * - Any Panopto player specific events
  * - Checking for iframe Panopto iframe initialization
  */
  initializePanoptoPlayer() {
    this.log('initializePanoptoPlayer()');
    if (!hasUserInteraction) this.initializeUserInteractionEvents();
    this.initPanoptoPlayerState();

    this.userAuthenticated = true;
    this.initTicks = 0;

    if (
      !this.options_.autoplay
      && this.options_.pauseImmediately
    ) {
      this.pauseImmediately = true;
    }

    if (this.src()?.src) {
      this.panoptoPlayer = this.instantiatePanoptoEmbedPlayer();
      this.hasLoadedPanoptoPlayer = true;
      if (this.debug) console.group('Panopto Player');
      this.log('Initialized the Panopto player...', this.panoptoPlayer);
      if (this.debug) console.groupEnd();
    }

    this.setupInternalPanoptoPlayerInitializationEvents();
    this.handlePanoptoPlayerIframeState();
  }

  instantiatePanoptoEmbedPlayer() {
    this.log('instantiating the Panopto Embed Player...');
    this.log('this.options_.panoptoBaseUrl: ', { panoptoBaseUrl: this.options_.panoptoBaseUrl });
    return new window.EmbedApi(this.el().id, {
      width: '100%',
      height: '100%',
      serverName: this.options_.panoptoBaseUrl,
      sessionId: this.activeVideoId,
      videoParams: {
        interactivity: 'none',
        hideoverlay: true,
        start: this.playerStartTime,
        autoplay: (this.options_.service === 'Designer'),
      },
      events: {
        onIframeReady: this.onIframeReady.bind(this),
        onReady: this.onPlayerReady.bind(this),
        onStateChange: this.onStateChange.bind(this),
        ...(_.isFunction(this.options_.onPanoptoLoginShown) && {
          onLoginShown: this.onLoginShown.bind(this),
        }),
        onError: this.onPlayerError.bind(this),
      },
    });
  }

  /**
   * Function that determines the correct state of the Panopto Player iframe.
   *
   * Could be one of 2 states:
   * - Successfully initialized
   *   - user has successfully authenticated (if need be), and they have the correct viewing permissions set in Panopto
   *     to view the video.
   * - Failed to initialize the Panopto Player embed
   *   - This is mainly caused by 2 factors:
   *     - The Panopto video / session has been deleted.
   *     - The user that is trying to view this Panopto video doesn't have the necessary or correct viewing permissions
   *       in Panopto for this video session.
   */
  handlePanoptoPlayerIframeState() {
    const latencyInterval = this.setInterval(() => {
      if (this.dynamicInitTickThreshold) {
        const {
          averageLatency,
          isFast,
          latencyReadings,
          threshold,
        } = this.options_?.vm?.internetLatencyData;
        if (!isFast) {
          /**
           * The numerical values used and referenced below, were taken from a multidude of bandwidth tests in conjunction
           * with investigative research.
           *
           * Basically, if the user has slow internet, we hard cap the `dynamicInitTickThreshold` value to 41. There are
           * 2 main reasons why that value is 41 (seconds):
           *    - We can't continue calling this function for > 60 seconds, since that is what our 'loaded' asynchronous watcher is
           *      set to in our Player.vue. Exceeding 60 seconds invokes our Player fallback logic which doesn't make
           *      sense in this situation, since we have clearly errored out.
           *    - Referencing Chrome's Network tab within the devtools -- the slowest throttling profile they give is
           *      Slow 3G which ends up reresenting this download and upload values:
           *        - Slow 3G Custom: Download 376 kb/s, Latency 2000 ms
           *          - So 376 (kb/s) / 100 = (37 + some latenc_variance = ~41)
           *          - we are dividing by 100, to somewhat normalize the download speed since that is measured in ms.
           *      Using this as a reference, we make sure to handle this bandwidth capacity, essentially setting an upper
           *      bound. At this point if for some reason the user has a slower internet connection, they should stop using
           *      a potatoe and I'd love to ask how did they travel back in time to the early 2000's. Oh yeah,
           *      and at those speeds, our application fails to serve up the bundled webpack assets.
           */
          let summationOfLatencyReadings = latencyReadings.reduce((sum, value) => sum + value, 0);
          this.log('original summation of latency readings: ', { summationOfLatencyReadings });
          // For very slow internet, our max threshold is 50 -- which was an average variance after a bunch of bandwidth tests
          summationOfLatencyReadings = (summationOfLatencyReadings < 50) ? (summationOfLatencyReadings / 100) : 41;
          this.log('final summation of latency readings: ', { summationOfLatencyReadings });
          this.dynamicInitTickThreshold = Math.round(1 + summationOfLatencyReadings + Panopto.initArray.length);
        }
        this.log('Clearing latencyInterval...');
        this.log({
          averageLatency,
          isFast,
          latencyReadings,
          threshold,
          dynamicInitTickThreshold: this.dynamicInitTickThreshold,
        });
        clearInterval(latencyInterval);
        return this.checkForPanoptoIframeInitialization();
      }
      this.dynamicInitTickThreshold = Math.round(
        1
        + (this.options_?.vm?.internetLatencyData?.averageLatency / 10)
        + Panopto.initArray.length,
      );
    });
  }

  // ############## Playback events methods specific to Panopto ##############
  /**
   * This event will be raised once authentication is complete and the iframe is ready to load the video.
   * A splash screen of the chosen video will be displayed in this state. If the video autoplays, it may skip this state.
   */
  onIframeReady() {
    this.log('onIframeReady() event');
    if (this.options_.service === 'Designer') this.panoptoPlayer.loadVideo();
    this.hasTriggeredFirstPlay = false;
    this.triggerReady();
    this.trigger('ready');
    this.trigger('loadstart');
    this.trigger('loadedmetadata');
    this.trigger('durationchange');
    // We are disabling the Panopto embed captions allowing our player to handle captions.
    this.panoptoPlayer.disableCaptions();
    if (!this.hasIframeInitialized) {
      this.setMuted(true);
      this.hasIframeInitialized = true;
    }
  }

  /**
   * This event occurs when playback begins, either by a call to loadVideo() or by the video loading automatically
   * in autoplay.
   */
  onPlayerReady() {
    this.log('onReady() event');
    Panopto.initArray = [];
    this.initTicks = 0;
    this.trigger('ready');
    this.trigger('canplay');
    if (this.playWhenReady) {
      this.playWhenReady = false;
      this.play();
    }
  }

  /**
   * This event will be raised when the video player’s state changes. The various values captured in the event are as follows:
   *
   * PlayerState (enum)
   *
   * PlayerState.Playing => 1
   * PlayerState.Paused => 2
   * PlayerState.Ended => 0
   *
   * @param {*} state
   */
  onStateChange(state) {
    this.log('onStateChange() event', { state });
    if (state === 0) {
      this._panoptoPlayerState.playing = false;
      this._panoptoPlayerState.ended = true;
      this.trigger('ended');
    } else if (state === 1) {
      this._panoptoPlayerState.playing = true;
      this._panoptoPlayerState.ended = false;
      this.trigger('timeupdate');
      this.trigger('durationchange');
      this.trigger('playing');
      this.trigger('play');
      if (this.pauseImmediately) {
        this.pauseImmediately = false;
        return this.pause();
      }
    } else if (state === 2) {
      this._panoptoPlayerState.playing = false;
      this.trigger('canplay');
      this.trigger('pause');
    }
    return true;
  }

  /**
   * This event will be raised when the login page is shown in cases where the user *needs to be authenticated*
   * before accessing the video.
   *
   * Note:        If the user is already authenticated, this will not fire.
   */
  onLoginShown() {
    this.log('onLoginShown() event');
    this.userAuthenticated = false;
    const iframeSrc = this.panoptoPlayer.iframe.src;
    this.log('auth iframe redirect url: ', { iframeSrc });
    this.panoptoPlayer = null;
    this.options_.onPanoptoLoginShown(iframeSrc);
  }

  /**
   * This event will be called when certain error conditions arise:
   *
   * Param: error -- type ApiError
   *
   * ApiError.PlayNotAllowed: This relates to browser autoplay policies, which may affect scenarios in
   * which the Panopto Embed iframe does not have permission to initiate playback without the user interacting
   * directly with the iframe. This error parameter appears if playback fails because the browser prevented programmatic
   * playback.
   *
   * ApiError.PlayWithSoundNotAllowed: This relates to browser autoplay policies, which may affect scenarios in which
   * the Panopto Embed iframe does not have permission to initiate playback without the user interacting directly with
   * the iframe. This error parameter appears if the video was muted because the browser prevented unmuted programmatic
   * playback.
   *
   * ApiError.MediaPlayerError: This indicates an error surfaced from the media player engine.
   *
   * ApiError.Unknown: This is reserved for future unspecified errors and is currently unused.
   */
  onPlayerError(apiError) {
    // For now, we just log the error we get back from Panopto.
    /* eslint no-console: "off" */
    this.log('onPlayerError() event ', { apiError });
  }
  // ############## End Playback events methods specific to Panopto ##############

  handleUserInteraction() {
    hasUserInteraction = true;
    this.hasUserInteraction = true;
  }

  /**
  * A function that listens for any click/scroll event.
  * Browser security standards mandate that a page has received user interaction before videos inside iframes can be unmuted.
  */
  initializeUserInteractionEvents() {
    document.removeEventListener('scroll', this.handleUserInteraction);
    document.removeEventListener('click', this.handleUserInteraction);
    document.addEventListener('scroll', this.handleUserInteraction, { once: true });
    document.addEventListener('click', this.handleUserInteraction, { once: true });
  }

  /**
    * A function that returns whether or not the video is currently buffering.
    *
    * @return {Boolean} If `true`, then the video is buffering.
    */
  buffering() {
    this.log('buffering...');
    return this.isBuffering;
  }

  /**
    * A getter/setter function that sets the `muted` property of the video (if an argument is provided) or
    * returns the current `muted` property of the video (if not).
    *
    * @param {Boolean} isMuted - If `true`, then mute the video. If `false`, unmute the video. If `undefined`,
    * return the current `this._childPlayerState.muted` value.
    *
    * @return {Boolean} - Represents whether the video is currently muted or not.
    */
  muted(isMuted) {
    this.log('muted()', { isMuted: this.panoptoPlayer.isMuted() });
    if ([null, undefined].includes(isMuted)) return this.panoptoPlayer.isMuted();
    return this.setMuted(isMuted);
  }

  /**
  * A function that sends a postMessage to the child player, instructing it to change its volume to
  * either 0 or 1 based on whether the video should be muted or not.
  *
  * @param {Boolean} isMuted - If `true`, then mute the video. If `false`, unmute the video. If `undefined`,
  * return the current `this._childPlayerState.muted` value.
  *
  * @return {Boolean} - Represents whether the video is currently muted or not.
  */
  setMuted(isMuted) {
    this.log('setMuted()', isMuted);
    if (isMuted) this.panoptoPlayer.muteVideo();
    else this.panoptoPlayer.unmuteVideo();
  }

  /**
  * A function that returns the volume value currently stored in the Panopto player.
  *
  * @return {Integer} volume - The volume of the player. Ranges from 0 to 1.
  */
  volume(volume) {
    this.log('volume()', this.panoptoPlayer.getVolume());
    if (typeof volume !== 'number') return this.panoptoPlayer.getVolume();
    return this.setVolume(volume);
  }

  /**
  * A function that sets the volume.
  *
  * @param {Integer} volume - The volume of the player. Ranges from 0 to 1.
  */
  setVolume(volume) {
    this.log('setVolume()', volume);
    this.panoptoPlayer.setVolume(volume);
  }

  /**
  * A function that returns the duration of the video, collected from the Panopto player.
  *
  * @return {Integer} An integer representing the length of the Panopto player's video in seconds.
  */
  duration() {
    this.log('duration()', this.panoptoPlayer.getDuration());
    return this.panoptoPlayer.getDuration();
  }

  /**
  * A getter/setter function that sets the `src` property of the video (if an argument is provided) or returns the
  * current `src` property of the video (if not).
  *
  * @param {String} src - A string representing the URL of the Proquest video.
  *
  * @return {Object} - An object containing the video source (String) as well as its type (String).
  */
  src(src) {
    if (src) this.setSrc({ src });
    return this.source;
  }

  /**
    * A function that sets the video's source before initializing the Panopto player.
    *
    * @param {Object} source - An object containing the video source (String) as well as its type (String).
    *
    * @return {Object} - An object containing the video source (String) as well as its type (String).
    */
  setSrc(source = {}) {
    if (!source || !source?.src) return;
    delete this.errorNumber; // todo tam
    this.source = source;
    this.activeVideoId = source.src;

    this.log('source && this.src():', {
      source,
      src: this.src(),
    });
    if (!Panopto.isScriptReady || source.src === this?.src()?.src) return;
    this.initializePanoptoPlayer();

    if (
      this.options_.autoplay
      && !_isOnMobile
    ) {
      if (this.isReady_) this.play();
      else this.playWhenReady = true;
    }
    return this.source;
  }

  /**
  * A getter/setter function that sets the `currentTime` property of the video (if an argument is provided) or
  * returns the current `currentTime` property of the video (if not).
  *
  * @param {Integer} time - A number representing the desired time for the video to seek to.
  *
  * @return {Integer} - A number representing the desired time for the video to seek to.
  */
  currentTime(time) {
    const currentTime = this.panoptoPlayer.getCurrentTime();
    if (
      this.isBuffering
      && parseFloat(currentTime?.toFixed(2)) === parseFloat(this.seekTime?.toFixed(2))
    ) {
      this.seekTime = null;
      this.trigger('seeked');
      this.isBuffering = false;
    }
    if (!_.isFinite(time)) return this.panoptoPlayer.getCurrentTime();
    return this.setCurrentTime(time);
  }

  /**
  * A function that instructs the Panopto player to seek to the provided time.
  *
  * @param {Integer} time - The desired time for the video to seek to.
  *
  * @return {Integer} - The desired time for the video to seek to.
  */
  setCurrentTime(time) {
    if (time === this.seekTime) return;
    this.log('setCurrentTime()', time);
    this.seekTime = time;
    this.isBuffering = true;
    this.trigger('seeking');
    return this.panoptoPlayer.seekTo(time);
  }

  /**
  * A function that returns the Panopto player's playback rate.
  */
  playbackRate() {
    this.log('playbackRate()', this.panoptoPlayer.getPlaybackRate());
    return this.panoptoPlayer.getPlaybackRate();
  }

  /**
    * A function that instructs the Panopto player to change its playback rate to the provided integer.
    *
    * @param {Integer} playbackRate - The desired playback rate for the video.
    *
    * @return {Integer} - The desired playback rate of the video.
    */
  setPlaybackRate(playbackRate) {
    this.log('setPlaybackRate()', playbackRate);
    return this.panoptoPlayer.setPlaybackRate(playbackRate);
  }

  /**
   * This function enables captions for a video. The optional parameter captionTrackIndex, allows you to specify which
   * caption track to enable
   */
  enableCaptions(captionIndex) {
    return this.panoptoPlayer.enableCaptions(captionIndex);
  }

  /**
    * A function that instructs the Panopto player to play.
    *
    * Since the Panopto player could sometimes be within an iframe, we can't fire off a play event until the user has
    * interacted with the page.
    *
    * use `initializeUserInteractionEvents()` to listen for either a click event or a scroll event, either of which serves as user
    * interaction for this purpose. Without the `setTimeout()` in the following code, the play event is triggered BEFORE the user
    * interaction click so we need `setTimeout()` here in order to ensure that the user interaction click is registered before the
    * play event.
    */
  play() {
    setTimeout(() => {
      this.log('play() 1');
      if (
        !this.hasLoadedPanoptoPlayer
        || !hasUserInteraction
        || this._panoptoPlayerState.playing
      ) {
        if (!this.hasLoadedPanoptoPlayer) this.playWhenReady = true;
        return;
      }
      this.log('play() 2');
      if (!this.hasTriggeredFirstPlay) {
        this.trigger('firstplay');
        this.hasTriggeredFirstPlay = true;
        this.panoptoPlayer.loadVideo();
        if (this.options_.service === 'PlayerInteractionVideo') this.panoptoPlayer.muteVideo();
      }
      this.trigger('play');
      return this.panoptoPlayer.playVideo();
    });
  }

  /**
  * A function that returns whether or not the Panopto player's video is currently playing.
  *
  * @return {Boolean} - If `true`, the Panopto player's video is playing. If `false`, it is not playing.
  */
  playing() {
    this.log('playing()', this._panoptoPlayerState.playing);
    if (this._panoptoPlayerState.playing) this.isBuffering = false;
    return this._panoptoPlayerState.playing;
  }

  /**
    * A function that returns whether or not the video is currently paused.
    *
    * @return {Boolean} If `true`, then the video is paused.
    */
  paused() {
    // this.log('paused()', !this._panoptoPlayerState.playing);
    return !this._panoptoPlayerState.playing;
  }

  /**
    * A function that instructs the Panopto player to pause.
    */
  pause() {
    this.log('pause()');
    return this.panoptoPlayer.pauseVideo();
  }

  /**
  * A function that returns whether or not the video has ended.
  *
  * @return {Boolean} - Represents whether the video has ended or not.
  */
  ended() {
    this.log('ended()', this._panoptoPlayerState.ended);
    return this._panoptoPlayerState.ended;
  }

  checkForPanoptoIframeInitialization() {
    Panopto.playerIframeInitIntervalArray.push(this.setInterval(() => {
      this.initTicks += 1;
      this.log({
        userAuthenticated: this.userAuthenticated,
        initTicks: this.initTicks,
        dynamicInitTickThreshold: this.dynamicInitTickThreshold,
        initTicksGreaterThanDynamicCalc: (this.initTicks > this.dynamicInitTickThreshold),
        hasIframeInitialized: this.hasIframeInitialized,
        metadatainitialized: this.options_.vm.bulbVideo.metadataInitialized,
        videoInitialized: this.options_.vm.bulbVideo.videoInitialized,
      });
      if (!this.userAuthenticated) return this.clearIframeInitializationInterval();
      if (this.hasIframeInitialized) {
        this.triggerReady();
        this.log('The Panopto VideoJS tech has finished initializing!');
        return this.trigger('playerIframeInitialized');
      }
      if (
        !this.hasIframeInitialized
        && !this.options_.vm.bulbVideo.metadataInitialized
        && !this.options_.vm.bulbVideo.videoInitialized
        && this.initTicks > this.dynamicInitTickThreshold
      ) {
        return this.trigger('playerIframeInitializationIssue');
      }
    }, 1000));
  }

  clearIframeInitializationInterval() {
    Panopto.playerIframeInitIntervalArray.forEach(clearInterval);
    Panopto.playerIframeInitIntervalArray = [];
  }

  /**
   * Function that setups the global debugging Panopto object.
   */
  setupDebugging() {
    if (window.btoa) {
      const encrypted = window.btoa(process.env.VUE_APP_DEVELOPER_CODE);
      window.panopto = (key) => {
        if (window.btoa(key) === encrypted) {
          return {
            this: this,
            panoptoPlayer: this.panoptoPlayer,
            panoptoVideoUrl: this.panoptoPlayer.getVideoUrl(),
            throwInitError: this.throwPanoptoInitializationError.bind(this),
          };
        }
        console.warn('The developer code you supplied is incorrect.');
      };
    } else {
      window.panopto = () => {
        console.warn('This browser does not support the "window.btoa" encryption function.');
      };
    }
  }

  /**
   * Helper debug function that invokes the explicitly invokes the Panopot Initialization error toast.
   * Mainly to allow QA ngs to test easier.
   */
  throwPanoptoInitializationError() {
    this.trigger('testPanoptoPlayerIframeInitializationIssue');
  }

  /**
   * Specific Panopto player events to subscribe to that help in intializaing the embedded player.
   * They are only listend for internally within this tech.
   */
  setupInternalPanoptoPlayerInitializationEvents() {
    this.on('playerIframeInitialized', () => { this.clearIframeInitializationInterval(); });
    this.on('playerIframeInitializationIssue', () => {
      this.clearIframeInitializationInterval();
      this.initTicks = 0;
      window.dispatchEvent(new CustomEvent('panoptoInitIssueToast'));
      this.log('creating the error out iframe...');
      const panoptoIframe = document.createElement('iframe');
      panoptoIframe.classList.add('panopto-video-issue-iframe-container');
      panoptoIframe.src = `https://${this.options_.panoptoBaseUrl}/Panopto/Pages/Embed.aspx?id=${this.activeVideoId}`;
      document.body.appendChild(panoptoIframe);
    });
    // ### Events only used for debugging and testing ###
    this.on('testPanoptoPlayerIframeInitializationIssue', () => {
      window.dispatchEvent(new CustomEvent('panoptoInitIssueToast'));
    });
  }

  /**
    * A function that's called whenever the parent player is destroyed so that the Panopto tech's main
    * element can be removed from the DOM.
    */
  dispose() {
    super.dispose();
  }
}

/**
* A function that determines whether or not the Panopto tech is supported.
*
* @return {Boolean} - true
*/
Panopto.isSupported = function isSupported() {
  return true;
};

/**
* A function that determines whether or not the Panopto tech supports fullscreen.
*
* @return {Boolean} - true
*/
Panopto.supportsFullScreen = function supportsFullScreen() {
  return true;
};

/**
  * A function that determines whether or not the provided video type matches the Panopto tech's supported video type.
  *
  * @return {Boolean} - If `true`, then the video type is supported. If `false`, then it is not.
  */
Panopto.canPlayType = function canPlayType(type) {
  return type === 'video/panopto';
};

/**
  * A function that determines whether or not the provided video src matches the expected video pattern AND whether or
  * not the provided video type matches the Panopto tech's supported video type.
  *
  * @param {String} type  - the video type.
  *
  * @return {Boolean} - If `true`, then the video src and type are supported. If `false`, then they are not.
  */
Panopto.canPlaySource = function canPlaySource({
  type,
} = {}) {
  return Panopto.canPlayType(type);
};

function scriptLoaded() {
  Panopto.isScriptReady = true;
}

/**
 * Inject the Panopto tech custom css.
 */
function injectCss() {
  const css = `
    .vjs-panopto { height: 100%; width: 100%; }
    .vjs-panopto .vjs-tech { position: absolute !important; }
    .vjs-panopto .vjs-poster { background-size: cover; }
    .vjs-panopto { max-width: 100%; }
    .vjs-panopto.vjs-fullscreen .panopto { height: 100% !important; width: 100% !important; }
  `;

  const head = document.head || document.getElementsByTagName('head')[0];
  const style = document.createElement('style');

  if (style.styleSheet) style.styleSheet.cssText = css;
  else style.appendChild(document.createTextNode(css));

  head.appendChild(style);
}

Panopto.initArray = [];
Panopto.playerIframeInitIntervalArray = [];

if (typeof document !== 'undefined') {
  loadScript('https://developers.panopto.com/scripts/embedapi.min.js', scriptLoaded);
  injectCss();
}

registerTech('Panopto', Panopto);
export default Panopto;

// For good measure: ┻━┻ ︵ヽ(`Д´)ﾉ︵┻━┻